import React, { useState, useRef, useEffect } from 'react';
import '../styles/PassionProjects.css';

// Image Assets
import ArtfulCribLogo from '../images/portfolio/artful-crib/logo.svg';
import ArtfulCribImg from '../images/portfolio/artful-crib/passion-project.png';
import BangleBandLogo from '../images/portfolio/bangle-and-band/logo.svg';
import BangleBandImg from '../images/portfolio/bangle-and-band/passion-project.png';

// Arrow SVGs
import ArrowLeft from '../images/portfolio/Arrow-Left.svg';
import ArrowRight from '../images/portfolio/Arrow-Right.svg';

const projects = [
  {
    id: 1,
    title: 'ArtfulCrib.com',
    url: 'https://artfulcrib.com',
    logo: ArtfulCribLogo,
    mockup: ArtfulCribImg,
    para1: 'is a curated online space dedicated to elevating the aesthetics of nursery decor through artistic inspiration and thoughtful design. As the creator, I envisioned and executed every aspect of the platform, from its user-friendly interface to its cohesive brand identity.',
    para2: "I led the design and development process, ensuring that every detail reflects the project's core mission of making artful living accessible. This passion project allowed me to merge my love for design and storytelling, showcasing my ability to create immersive and impactful digital experiences."
  },
  {
    id: 2,
    title: 'Bangle & Band',
    url: 'https://bangleandband.com',
    logo: BangleBandLogo,
    mockup: BangleBandImg,
    para1: 'is a vibrant online storefront showcasing the ingenuity of my jewelry pieces. Born from my vision to beautifully conceal a hair tie, I led every aspect of the project. From product design and manufacturing to creating a distinctive brand identity that resonates with both wholesalers and customers. Through meticulous attention to detail, I developed a seamless e-commerce experience that emphasizes elegance and accessibility.',
    para2: 'This project highlights my ability to take an idea from concept to production, blending creativity, technical expertise, and strategic branding into a cohesive and impactful platform.'
  }
];

const PassionProjects = () => {
  const [activeIndex, setActiveIndex] = useState(0); // 0 = ArtfulCrib, 1 = Bangle & Band
  const cardRef = useRef(null);
  const [shiftDistance, setShiftDistance] = useState(0);

  useEffect(() => {
    const updateDistance = () => {
      if (cardRef.current) {
        const cardWidth = cardRef.current.offsetWidth;
        const wrapper = cardRef.current.closest('.passion-projects-carousel-wrapper');
        const paddingLeft = wrapper ? parseFloat(window.getComputedStyle(wrapper).paddingLeft) || 0 : 0;
        // Shift cardWidth + gap + paddingLeft so Card 2 aligns cleanly inside viewport without right cutoff
        setShiftDistance(cardWidth + 32 + paddingLeft);
      }
    };
    updateDistance();
    window.addEventListener('resize', updateDistance);
    return () => window.removeEventListener('resize', updateDistance);
  }, []);

  const handleScrollCarousel = (direction) => {
    if (direction === 'right') {
      setActiveIndex(1);
    } else {
      setActiveIndex(0);
    }
  };

  return (
    <section id="passion-projects" className="passion-projects-section py-5 position-relative">
      <div className="container">
        {/* Section Header */}
        <div className="passion-projects-header text-start mb-4">
          <h1 className="passion-projects-title mb-3">Passion Projects</h1>
          <p className="passion-projects-subtitle">
            Designing and shipping enterprise products at scale—backed by measurable impact.
          </p>
        </div>

        {/* Controls Row */}
        <div className="passion-projects-controls d-flex align-items-center gap-3 mb-4">
          <span className="passion-page-indicator m-0">
            Projects <span className="passion-page-fraction">{activeIndex + 1}/2</span>
          </span>
          <div className="d-flex gap-2">
            <button
              className={`passion-arrow-btn ${activeIndex === 0 ? 'disabled-btn' : ''}`}
              onClick={() => handleScrollCarousel('left')}
              aria-label="Previous Passion Project"
            >
              <img src={ArrowLeft} alt="Previous" />
            </button>
            <button
              className={`passion-arrow-btn ${activeIndex === 1 ? 'disabled-btn' : ''}`}
              onClick={() => handleScrollCarousel('right')}
              aria-label="Next Passion Project"
            >
              <img src={ArrowRight} alt="Next" />
            </button>
          </div>
        </div>
      </div>

      {/* Horizontal Carousel Track Outer Wrapper */}
      <div className="passion-projects-carousel-wrapper">
        <div
          className="passion-projects-carousel-track"
          style={{
            transform: `translateX(-${activeIndex * shiftDistance}px)`,
            transition: 'transform 0.45s cubic-bezier(0.25, 1, 0.5, 1)',
          }}
        >
          {projects.map((item, idx) => (
            <div
              key={item.id}
              ref={idx === 0 ? cardRef : null}
              className="passion-card-item flex-shrink-0"
            >
              <div className="passion-card h-100">
                <div className="row align-items-center h-100 g-4">
                  <div className="col-lg-6 text-start pe-lg-3 mb-4 mb-lg-0">
                    <img 
                      src={item.logo} 
                      alt={`${item.title} Logo`}
                      className="passion-card-logo mb-4" 
                    />
                    <p className="passion-card-text">
                      <a 
                        href={item.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="passion-card-link"
                      >
                        {item.title}
                      </a>{' '}
                      {item.para1}
                    </p>
                    <p className="passion-card-text m-0">
                      {item.para2}
                    </p>
                  </div>
                  <div className="col-lg-6 text-center">
                    <img
                      src={item.mockup}
                      alt={`${item.title} Mockups`}
                      className="passion-card-mockup img-fluid"
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PassionProjects;